import { instantiate, Node, NodePool, Prefab, warn, _decorator } from 'cc';
import { DEV } from 'cc/env';
import FrameActionExecutor from '../Utils/FrameActionExecutor';

const { ccclass, property } = _decorator;

@ccclass('NodePoolInstance')
export default class NodePoolInstance {
    @property({ type: Node, formerlySerializedAs: 'root' })
    private _root: Node = null;

    @property({
        type: Prefab,
        formerlySerializedAs: 'prefab',
    })
    private _prefab: Prefab = null;

    @property(Node)
    private _clonedNode: Node = null;

    private _pool: NodePool = null;
    private _instantiateCallback: (node: Node) => void = null;
    private _instantiatedCount: number = 0;

    @property({
        type: Node,
        tooltip: DEV && 'The root node to which the instantiated nodes will be parented',
    })
    public get root() {
        return this._root;
    }
    public set root(value: Node) {
        if (this._root === value) {
            return;
        }
        this._root = value;
    }

    @property({
        type: Node,
        visible(this: NodePoolInstance) {
            return !this._prefab;
        },
    })
    public get clonedNode() {
        return this._clonedNode;
    }
    public set clonedNode(value: Node) {
        if (this._prefab) {
            warn('Current instance is using prefab, cannot set clonedNode');
            return;
        }
        if (this._clonedNode === value) {
            return;
        }
        this._clonedNode = value;
    }

    @property({
        type: Prefab,
        visible(this: NodePoolInstance) {
            return !this._clonedNode;
        },
    })
    public get prefab() {
        return this._prefab;
    }
    public set prefab(value: Prefab) {
        if (this._clonedNode) {
            warn('Current instance is using clonedNode, cannot set prefab');
            return;
        }
        if (this._prefab === value) {
            return;
        }
        this._prefab = value;
    }

    public get size() {
        return this.pool.size();
    }

    private get pool() {
        if (!this._pool) {
            this._pool = new NodePool();
        }
        return this._pool;
    }

    public init(instantiateCallback?: (node: Node) => void, poolHandleComp?: ConstructorParameters<typeof NodePool>[number]) {
        if (this._pool) {
            throw new Error('NodePoolInstance already initialized');
        }
        this._pool = new NodePool(poolHandleComp);
        this._instantiateCallback = instantiateCallback;
    }

    public prespawn(count: number): Promise<Node[]> {
        if (count <= 0) {
            throw new Error('Count must be greater than 0');
        }
        const executor = new FrameActionExecutor(
            () => {
                const node = this._instantiateNode();
                this.pool.put(node);
                if (this._instantiatedCount >= count) {
                    executor.stop();
                }
                return node;
            },
            {
                timesPerFrame: 1,
                totalTimes: count,
            },
        );
        const promise = executor.start();
        return promise;
    }

    public get(): Node {
        const node = this.pool.get() ?? this._instantiateNode();
        if (this._root) {
            node.setParent(this._root);
        }
        return node;
    }

    public put(node: Node) {
        this.pool.put(node);
    }

    public clear() {
        this.pool.clear();
    }

    private _instantiateNode() {
        const node = instantiate(this._prefab ?? this._clonedNode) as Node;
        this._instantiateCallback?.(node);
        this._instantiatedCount++;
        return node;
    }
}
